import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { toast } from 'sonner';
import RecordLookupSelect from '../RecordLookupSelect';
import { useGlobalMetadata } from '../../utils/providers';
import { useRecordNavigator } from '../../hooks/useRecordNavigator';
import { queryPolicies } from '../../utils/queryPolicies';
import type { BaseRecord } from '../../types/api';
import type { RelationshipLookup } from '../../types/metadata';
import { fetchTargetFields, promoteLead } from './api';
import { leadImportKeys } from './keys';
import { ErrorNote, Segmented } from './ui';

type Mode = 'create' | 'attach';

/** Turn one lead into a merchant: create a new merchant from its values, or
 * attach it to a merchant that already exists. Every lead with the same phone
 * follows. */
export function MerchantFromLeadDialog({ lead, source, canCreate, suggested, onClose }: {
  /** The lead row; values are read by lead column name. */
  lead: { id: number; phone_normalized: string | null } & Record<string, unknown>; source: string | null | undefined; canCreate: boolean;
  /** A merchant that already holds this phone; preselected for attaching. */
  suggested?: { id: number; label: string | null }; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { openRecord } = useRecordNavigator();
  const { data: metadata } = useGlobalMetadata();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const relationship = (metadata as any)?.relationshipLookupData?.lead_import_row?.byFieldName?.merchant as RelationshipLookup | undefined;
  const [mode, setMode] = useState<Mode>(canCreate ? 'create' : 'attach');
  const [merchant, setMerchant] = useState<BaseRecord | null>(
    suggested ? { id: suggested.id, business_name: suggested.label || `Merchant #${suggested.id}` } as unknown as BaseRecord : null);

  // The same columns lead_promote copies: text fields of the import vocabulary.
  const targets = useQuery({ queryKey: leadImportKeys.targets, queryFn: fetchTargetFields, ...queryPolicies.metadata });
  const copied = (targets.data ?? []).filter((t) =>
    (t.merchant_data_type === 'text' || t.merchant_data_type === 'character varying') && String(lead[t.lead_column] ?? '').trim());

  const promote = useMutation({
    mutationFn: () => promoteLead(lead.id, mode, mode === 'attach' ? Number(merchant?.id) : null),
    onSuccess: (r) => {
      toast.success(r.mode === 'create' ? `Merchant #${r.merchant} created.` : `Lead attached to merchant #${r.merchant}.`, {
        action: { label: 'Open merchant', onClick: () => openRecord({ tableName: 'merchant', id: r.merchant }) },
      });
      queryClient.invalidateQueries({ queryKey: leadImportKeys.all });
      onClose();
    },
  });

  const ready = mode === 'create' || merchant != null;

  return (
    <Dialog isOpen onClose={onClose} title="Make this lead a merchant" className="li-mfl">
      <DialogBody>
        {canCreate ? (
          <Segmented<Mode>
            ariaLabel="How to link this lead"
            value={mode}
            onChange={(m) => { setMode(m); promote.reset(); }}
            options={[{ value: 'create', label: 'Create a new merchant' }, { value: 'attach', label: 'Attach to an existing one' }]}
          />
        ) : (
          <p>This phone already belongs to a merchant. Attach the lead to it to link them.</p>
        )}
        {mode === 'create' ? (
          <div className="li-mfl__pane">
            <p className="li-muted">The new merchant starts with status Lead, lead source {source ? <b>{source}</b> : 'of this import'}, and these values:</p>
            {targets.isPending ? <p className="li-muted">Loading…</p> : (
              <dl className="li-dl li-mfl__fields">
                {copied.map((t) => (
                  <div key={t.field_name} className="li-mfl__field"><dt>{t.ui_label}</dt><dd>{String(lead[t.lead_column])}</dd></div>
                ))}
              </dl>
            )}
            <p className="li-muted">No main rep is set. Assign one on the merchant afterwards.</p>
          </div>
        ) : (
          <div className="li-mfl__pane">
            <p className="li-muted">Search by business name. The merchant keeps its own values; only the link is added.</p>
            {relationship
              ? <RecordLookupSelect name="merchant" relationship={relationship} record={merchant} onChange={setMerchant} schema="public" title="Merchant" />
              : <p className="li-muted">Merchant search is not available.</p>}
          </div>
        )}
        <p className="li-muted li-mfl__same">Every lead with phone <span className="li-mono">{lead.phone_normalized}</span> is linked too.</p>
        <ErrorNote error={promote.error} />
      </DialogBody>
      <DialogFooter actions={<>
        <button type="button" className="li-btn li-btn--quiet" onClick={onClose}>Cancel</button>
        <button type="button" className="li-btn li-btn--primary" disabled={!ready || promote.isPending} onClick={() => promote.mutate()}>
          {promote.isPending ? 'Saving…' : mode === 'create' ? 'Create merchant' : 'Attach to merchant'}
        </button>
      </>} />
    </Dialog>
  );
}
